import { createAction } from '@reduxjs/toolkit';
import { buildReducer } from '../../utils/redux/getReduxModuleByFeatureName';

// @OWNER_WAGE

const initialState = {
  // wageScale
  wageScale: null,
  wageScaleLoading: false,
  wageScaleError: null,

  // wageBonuses
  wageBonuses: null,
  wageBonusesLoading: false,
  wageBonusesError: null,

  // wageScaleTypes
  wageScaleTypes: null,
  wageScaleTypesLoading: false,
  wageScaleTypesError: null,
  wageType: 'normal',

  // wageScaleNationality
  wageScaleNationalities: null,
  wageScaleNationalitiesLoading: false,
  wageScaleNationalitiesError: null,
  wageNationality: null,

  // nationalityScale
  nationalityScaleLoading: false,
  nationalityScaleError: null,

  // CREW-1101 — the components this owner's matrix is built from, in column
  // order, and the shared catalogue they are picked from. `wageComponents` is
  // never empty: an owner nobody has configured gets the seven built-in lines.
  wageComponents: null,
  wageComponentsLoading: false,
  wageComponentsConfigured: false,
  componentCatalog: null,
  componentCatalogLoading: false,
  componentError: null,

  // The matrix's ROWS. `matrixRanks` is what this owner has cards for, one
  // entry per rank with the two reasons it may not be dropped; `allRanks` is
  // the catalogue they are ticked out of, the same list the PPE matrix
  // offers.
  matrixRanks: null,
  allRanks: null,
  matrixRanksLoading: false,
  rankError: null,

  // CREW-1299 — the same matrix UI on the vessel page. `vesselMatrix` says
  // whose matrix the vessel is paid from: `{ vessel_id, owner_id, owner_name,
  // overridden }`. Null on the owner's page, where every saga reads the owner
  // straight from the URL.
  vesselMatrix: null,
  vesselMatrixLoading: false,
  vesselMatrixError: null,
}

export const constants = {
  LOAD_WAGE_SCALE_REQUEST: '@OWNER_WAGE/LOAD_WAGE_SCALE_REQUEST',
  LOAD_WAGE_SCALE_SUCCESS: '@OWNER_WAGE/LOAD_WAGE_SCALE_SUCCESS',
  LOAD_WAGE_SCALE_ERROR: '@OWNER_WAGE/LOAD_WAGE_SCALE_ERROR',
  UPDATE_WAGE_SCALE_REQUEST: '@OWNER_WAGE/UPDATE_WAGE_SCALE_REQUEST',

  LOAD_WAGE_BONUSES_REQUEST: '@OWNER_WAGE/LOAD_WAGE_BONUSES_REQUEST',
  LOAD_WAGE_BONUSES_SUCCESS: '@OWNER_WAGE/LOAD_WAGE_BONUSES_SUCCESS',
  LOAD_WAGE_BONUSES_ERROR: '@OWNER_WAGE/LOAD_WAGE_BONUSES_ERROR',
  UPDATE_WAGE_BONUSES_REQUEST: '@OWNER_WAGE/UPDATE_WAGE_BONUSES_REQUEST',

  LOAD_WAGE_SCALE_TYPES_REQUEST: '@OWNER_WAGE/LOAD_WAGE_SCALE_TYPES_REQUEST',
  LOAD_WAGE_SCALE_TYPES_SUCCESS: '@OWNER_WAGE/LOAD_WAGE_SCALE_TYPES_SUCCESS',
  LOAD_WAGE_SCALE_TYPES_ERROR: '@OWNER_WAGE/LOAD_WAGE_SCALE_TYPES_ERROR',

  LOAD_WAGE_NATIONALITY_REQUEST: '@OWNER_WAGE/LOAD_WAGE_NATIONALITY_REQUEST',
  LOAD_WAGE_NATIONALITY_SUCCESS: '@OWNER_WAGE/LOAD_WAGE_NATIONALITY_SUCCESS',
  LOAD_WAGE_NATIONALITY_ERROR: '@OWNER_WAGE/LOAD_WAGE_NATIONALITY_ERROR',

  WAGE_TYPE_CHANGE: '@OWNER_WAGE/WAGE_TYPE_CHANGE',
  WAGE_NATIONALITY_CHANGE: '@OWNER_WAGE/WAGE_NATIONALITY_CHANGE',

  CREATE_NATIONALITY_SCALE_REQUEST: '@OWNER_WAGE/CREATE_NATIONALITY_SCALE_REQUEST',
  CREATE_NATIONALITY_SCALE_SUCCESS: '@OWNER_WAGE/CREATE_NATIONALITY_SCALE_SUCCESS',
  CREATE_NATIONALITY_SCALE_ERROR: '@OWNER_WAGE/CREATE_NATIONALITY_SCALE_ERROR',

  LOAD_WAGE_COMPONENTS_REQUEST: '@OWNER_WAGE/LOAD_WAGE_COMPONENTS_REQUEST',
  LOAD_WAGE_COMPONENTS_SUCCESS: '@OWNER_WAGE/LOAD_WAGE_COMPONENTS_SUCCESS',
  LOAD_WAGE_COMPONENTS_ERROR: '@OWNER_WAGE/LOAD_WAGE_COMPONENTS_ERROR',

  LOAD_COMPONENT_CATALOG_REQUEST: '@OWNER_WAGE/LOAD_COMPONENT_CATALOG_REQUEST',
  LOAD_COMPONENT_CATALOG_SUCCESS: '@OWNER_WAGE/LOAD_COMPONENT_CATALOG_SUCCESS',
  LOAD_COMPONENT_CATALOG_ERROR: '@OWNER_WAGE/LOAD_COMPONENT_CATALOG_ERROR',

  ADD_OWNER_COMPONENT_REQUEST: '@OWNER_WAGE/ADD_OWNER_COMPONENT_REQUEST',
  REMOVE_OWNER_COMPONENT_REQUEST: '@OWNER_WAGE/REMOVE_OWNER_COMPONENT_REQUEST',
  CREATE_WAGE_COMPONENT_REQUEST: '@OWNER_WAGE/CREATE_WAGE_COMPONENT_REQUEST',
  SET_COMPONENT_PRINT_CELL_REQUEST: '@OWNER_WAGE/SET_COMPONENT_PRINT_CELL_REQUEST',
  COMPONENT_ERROR: '@OWNER_WAGE/COMPONENT_ERROR',
  CLEAR_COMPONENT_ERROR: '@OWNER_WAGE/CLEAR_COMPONENT_ERROR',

  LOAD_MATRIX_RANKS_REQUEST: '@OWNER_WAGE/LOAD_MATRIX_RANKS_REQUEST',
  LOAD_MATRIX_RANKS_SUCCESS: '@OWNER_WAGE/LOAD_MATRIX_RANKS_SUCCESS',
  LOAD_ALL_RANKS_SUCCESS: '@OWNER_WAGE/LOAD_ALL_RANKS_SUCCESS',
  APPLY_MATRIX_RANKS_REQUEST: '@OWNER_WAGE/APPLY_MATRIX_RANKS_REQUEST',
  RANK_ERROR: '@OWNER_WAGE/RANK_ERROR',
  CLEAR_RANK_ERROR: '@OWNER_WAGE/CLEAR_RANK_ERROR',

  LOAD_VESSEL_MATRIX_REQUEST: '@OWNER_WAGE/LOAD_VESSEL_MATRIX_REQUEST',
  // Fired whenever the vessel's matrix is known or has just switched — the
  // vessel page's counterpart of the owner page's ADD_LOADED_ITEM: every
  // table on the tab is (re)read from it.
  VESSEL_MATRIX_LOADED: '@OWNER_WAGE/VESSEL_MATRIX_LOADED',
  VESSEL_MATRIX_ERROR: '@OWNER_WAGE/VESSEL_MATRIX_ERROR',
  OVERRIDE_VESSEL_MATRIX_REQUEST: '@OWNER_WAGE/OVERRIDE_VESSEL_MATRIX_REQUEST',
  RESET_VESSEL_MATRIX_REQUEST: '@OWNER_WAGE/RESET_VESSEL_MATRIX_REQUEST',
};

export const actions = {
  loadWageScaleRequest: createAction(constants.LOAD_WAGE_SCALE_REQUEST),
  loadWageScaleSuccess: createAction(constants.LOAD_WAGE_SCALE_SUCCESS),
  loadWageScaleError: createAction(constants.LOAD_WAGE_SCALE_ERROR),
  updateWageScaleRequest: createAction(constants.UPDATE_WAGE_SCALE_REQUEST),

  loadWageBonusesRequest: createAction(constants.LOAD_WAGE_BONUSES_REQUEST),
  loadWageBonusesSuccess: createAction(constants.LOAD_WAGE_BONUSES_SUCCESS),
  loadWageBonusesError: createAction(constants.LOAD_WAGE_BONUSES_ERROR),
  updateWageBonusesRequest: createAction(constants.UPDATE_WAGE_BONUSES_REQUEST),

  loadWageScaleTypesRequest: createAction(constants.LOAD_WAGE_SCALE_TYPES_REQUEST),
  loadWageScaleTypesSuccess: createAction(constants.LOAD_WAGE_SCALE_TYPES_SUCCESS),
  loadWageScaleTypesError: createAction(constants.LOAD_WAGE_SCALE_TYPES_ERROR),

  loadWageNationalitiesRequest: createAction(constants.LOAD_WAGE_NATIONALITY_REQUEST),
  loadWageNationalitiesSuccess: createAction(constants.LOAD_WAGE_NATIONALITY_SUCCESS),
  loadWageNationalitiesError: createAction(constants.LOAD_WAGE_NATIONALITY_ERROR),

  wageTypeChange: createAction(constants.WAGE_TYPE_CHANGE),
  wageNationalityChange: createAction(constants.WAGE_NATIONALITY_CHANGE),

  createNationalityScaleRequest: createAction(constants.CREATE_NATIONALITY_SCALE_REQUEST),
  createNationalityScaleSuccess: createAction(constants.CREATE_NATIONALITY_SCALE_SUCCESS),
  createNationalityScaleError: createAction(constants.CREATE_NATIONALITY_SCALE_ERROR),

  loadWageComponentsRequest: createAction(constants.LOAD_WAGE_COMPONENTS_REQUEST),
  loadWageComponentsSuccess: createAction(constants.LOAD_WAGE_COMPONENTS_SUCCESS),
  loadWageComponentsError: createAction(constants.LOAD_WAGE_COMPONENTS_ERROR),

  loadComponentCatalogRequest: createAction(constants.LOAD_COMPONENT_CATALOG_REQUEST),
  loadComponentCatalogSuccess: createAction(constants.LOAD_COMPONENT_CATALOG_SUCCESS),
  loadComponentCatalogError: createAction(constants.LOAD_COMPONENT_CATALOG_ERROR),

  addOwnerComponent: createAction(constants.ADD_OWNER_COMPONENT_REQUEST),
  removeOwnerComponent: createAction(constants.REMOVE_OWNER_COMPONENT_REQUEST),
  createWageComponent: createAction(constants.CREATE_WAGE_COMPONENT_REQUEST),
  setComponentPrintCell: createAction(constants.SET_COMPONENT_PRINT_CELL_REQUEST),
  componentError: createAction(constants.COMPONENT_ERROR),
  clearComponentError: createAction(constants.CLEAR_COMPONENT_ERROR),

  loadMatrixRanksRequest: createAction(constants.LOAD_MATRIX_RANKS_REQUEST),
  loadMatrixRanksSuccess: createAction(constants.LOAD_MATRIX_RANKS_SUCCESS),
  loadAllRanksSuccess: createAction(constants.LOAD_ALL_RANKS_SUCCESS),
  applyMatrixRanks: createAction(constants.APPLY_MATRIX_RANKS_REQUEST),
  rankError: createAction(constants.RANK_ERROR),
  clearRankError: createAction(constants.CLEAR_RANK_ERROR),

  loadVesselMatrix: createAction(constants.LOAD_VESSEL_MATRIX_REQUEST),
  vesselMatrixLoaded: createAction(constants.VESSEL_MATRIX_LOADED),
  vesselMatrixError: createAction(constants.VESSEL_MATRIX_ERROR),
  overrideVesselMatrix: createAction(constants.OVERRIDE_VESSEL_MATRIX_REQUEST),
  resetVesselMatrix: createAction(constants.RESET_VESSEL_MATRIX_REQUEST),
};

const actionHandlers = {
  [constants.LOAD_WAGE_SCALE_REQUEST]: (state) => ({ ...state, wageScaleLoading: true, wageScale: null }),
  [constants.LOAD_WAGE_SCALE_SUCCESS]: (state, { payload: { data } }) => ({
    ...state,
    wageScale: data,
    wageScaleLoading: false,
  }),
  [constants.LOAD_WAGE_SCALE_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    wageScaleError: error,
    wageScaleLoading: false,
  }),

  [constants.LOAD_WAGE_BONUSES_REQUEST]: (state) => ({ ...state, wageBonusesLoading: true, wageBonuses: null }),
  [constants.LOAD_WAGE_BONUSES_SUCCESS]: (state, { payload: { data } }) => ({
    ...state,
    wageBonuses: data,
    wageBonusesLoading: false,
  }),
  [constants.LOAD_WAGE_BONUSES_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    wageBonusesError: error,
    wageBonusesLoading: false,
  }),

  [constants.LOAD_WAGE_SCALE_TYPES_REQUEST]: (state) => ({ ...state, wageScaleTypesLoading: true }),
  [constants.LOAD_WAGE_SCALE_TYPES_SUCCESS]: (state, { payload: { data } }) => ({
    ...state,
    wageScaleTypes: data,
    wageScaleTypesLoading: false,
  }),
  [constants.LOAD_WAGE_SCALE_TYPES_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    wageScaleTypesError: error,
    wageScaleTypesLoading: false,
  }),

  [constants.WAGE_TYPE_CHANGE]: (state, { payload: wageType }) => ({
    ...state,
    wageType,
  }),

  [constants.LOAD_WAGE_NATIONALITY_REQUEST]: (state) => ({ ...state, wageScaleNationalitiesLoading: true }),
  [constants.LOAD_WAGE_NATIONALITY_SUCCESS]: (state, { payload: { data } }) => ({
    ...state,
    wageScaleNationalities: data,
    wageScaleNationalitiesLoading: false,
  }),
  [constants.LOAD_WAGE_NATIONALITY_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    wageScaleNationalitiesError: error,
    wageScaleNationalitiesLoading: false,
  }),

  [constants.WAGE_NATIONALITY_CHANGE]: (state, { payload: wageNationality }) => ({
    ...state,
    wageNationality,
  }),

  [constants.CREATE_NATIONALITY_SCALE_REQUEST]: (state) => ({ ...state, nationalityScaleLoading: true }),
  [constants.CREATE_NATIONALITY_SCALE_SUCCESS]: (state) => ({
    ...state,
    nationalityScaleLoading: false,
  }),
  [constants.CREATE_NATIONALITY_SCALE_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    nationalityScaleError: error,
    nationalityScaleLoading: false,
  }),

  [constants.LOAD_WAGE_COMPONENTS_REQUEST]: (state) => ({ ...state, wageComponentsLoading: true }),
  [constants.LOAD_WAGE_COMPONENTS_SUCCESS]: (state, { payload: { components, configured } }) => ({
    ...state,
    wageComponents: components,
    wageComponentsConfigured: configured,
    wageComponentsLoading: false,
  }),
  [constants.LOAD_WAGE_COMPONENTS_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    componentError: error,
    wageComponentsLoading: false,
  }),

  [constants.LOAD_COMPONENT_CATALOG_REQUEST]: (state) => ({ ...state, componentCatalogLoading: true }),
  [constants.LOAD_COMPONENT_CATALOG_SUCCESS]: (state, { payload: catalog }) => ({
    ...state,
    componentCatalog: catalog,
    componentCatalogLoading: false,
  }),
  [constants.LOAD_COMPONENT_CATALOG_ERROR]: (state, { payload: { error } }) => ({
    ...state,
    componentError: error,
    componentCatalogLoading: false,
  }),

  // The backend refuses to drop a column that still carries figures, and to
  // archive a component somebody is paying. Both come back as a message meant
  // to be read, so keep it rather than only logging it.
  [constants.COMPONENT_ERROR]: (state, { payload: message }) => ({ ...state, componentError: message }),
  [constants.CLEAR_COMPONENT_ERROR]: (state) => ({ ...state, componentError: null }),

  [constants.LOAD_MATRIX_RANKS_REQUEST]: (state) => ({ ...state, matrixRanksLoading: true }),
  [constants.LOAD_MATRIX_RANKS_SUCCESS]: (state, { payload: matrixRanks }) => ({
    ...state,
    matrixRanks,
    matrixRanksLoading: false,
  }),
  [constants.LOAD_ALL_RANKS_SUCCESS]: (state, { payload: allRanks }) => ({ ...state, allRanks }),

  // Same shape as the component refusals beside it: the backend answers a
  // blocked removal with a sentence naming the ranks, and that sentence is
  // the only thing that tells the coordinator which box to tick back.
  [constants.RANK_ERROR]: (state, { payload: message }) => ({ ...state, rankError: message, matrixRanksLoading: false }),
  [constants.CLEAR_RANK_ERROR]: (state) => ({ ...state, rankError: null }),

  // Opening the tab, or switching the vessel between the owner's matrix and
  // its own, starts from the normal scale: an alternative nationality picked
  // on the other matrix may not exist on this one, and the table would come
  // back empty for no visible reason.
  [constants.LOAD_VESSEL_MATRIX_REQUEST]: (state) => ({
    ...state,
    vesselMatrix: null,
    vesselMatrixLoading: true,
    vesselMatrixError: null,
    wageType: 'normal',
    wageNationality: null,
  }),
  [constants.OVERRIDE_VESSEL_MATRIX_REQUEST]: (state) => ({ ...state, vesselMatrixLoading: true, vesselMatrixError: null }),
  [constants.RESET_VESSEL_MATRIX_REQUEST]: (state) => ({ ...state, vesselMatrixLoading: true, vesselMatrixError: null }),
  [constants.VESSEL_MATRIX_LOADED]: (state, { payload: vesselMatrix }) => ({
    ...state,
    vesselMatrix,
    vesselMatrixLoading: false,
    wageType: state.vesselMatrix?.overridden === vesselMatrix?.overridden ? state.wageType : 'normal',
    wageNationality: state.vesselMatrix?.overridden === vesselMatrix?.overridden ? state.wageNationality : null,
  }),
  [constants.VESSEL_MATRIX_ERROR]: (state, { payload: message }) => ({
    ...state,
    vesselMatrixError: message,
    vesselMatrixLoading: false,
  }),
};
export const reducer = buildReducer(initialState, actionHandlers);

export const selector = (state) => ({
  wageScale: state.ownerDetailsWage.wageScale,
  wageScaleLoading: state.ownerDetailsWage.wageScaleLoading,
  wageScaleError: state.ownerDetailsWage.wageScaleError,

  wageBonuses: state.ownerDetailsWage.wageBonuses,
  wageBonusesLoading: state.ownerDetailsWage.wageBonusesLoading,
  wageBonusesError: state.ownerDetailsWage.wageBonusesError,

  wageScaleTypes: state.ownerDetailsWage.wageScaleTypes,
  wageScaleTypesLoading: state.ownerDetailsWage.wageScaleTypesLoading,
  wageScaleTypesError: state.ownerDetailsWage.wageScaleTypesError,

  wageScaleNationalities: state.ownerDetailsWage.wageScaleNationalities,
  wageScaleNationalitiesLoading: state.ownerDetailsWage.wageScaleNationalitiesLoading,
  wageScaleNationalitiesError: state.ownerDetailsWage.wageScaleNationalitiesError,

  wageType: state.ownerDetailsWage.wageType,
  wageNationality: state.ownerDetailsWage.wageNationality,

  nationalityScaleLoading: state.ownerDetailsWage.nationalityScaleLoading,
  nationalityScaleError: state.ownerDetailsWage.nationalityScaleError,

  wageComponents: state.ownerDetailsWage.wageComponents,
  wageComponentsLoading: state.ownerDetailsWage.wageComponentsLoading,
  wageComponentsConfigured: state.ownerDetailsWage.wageComponentsConfigured,
  componentCatalog: state.ownerDetailsWage.componentCatalog,
  componentCatalogLoading: state.ownerDetailsWage.componentCatalogLoading,
  componentError: state.ownerDetailsWage.componentError,

  matrixRanks: state.ownerDetailsWage.matrixRanks,
  allRanks: state.ownerDetailsWage.allRanks,
  matrixRanksLoading: state.ownerDetailsWage.matrixRanksLoading,
  rankError: state.ownerDetailsWage.rankError,

  vesselMatrix: state.ownerDetailsWage.vesselMatrix,
  vesselMatrixLoading: state.ownerDetailsWage.vesselMatrixLoading,
  vesselMatrixError: state.ownerDetailsWage.vesselMatrixError,
});
